import { z } from "zod";
import { useContext } from "react";
import { ConfigContext } from "../../util/ConfigContext";
import { DynamicForm } from "../../util/DynamicForm";
import { useGeneratedForm } from "../../hooks/useGeneratedForm";
import API_URL from "../../config/apiConfig";
import { apiPut } from "../../util/axiosUtil";
import MutationHandler from "../../util/MutationHandler";

export default function ZoomEdit({ onCancel, data, onSuccess }) {
  const { jwt } = useContext(ConfigContext);
  const isVideoTenant = jwt.iv;
  const formConfig = {
    email: {
      label: "Email",
      type: "email",
      placeholder: "Enter your Email",
      defaultValue: data.email,
      required: true,
      validation: z.string().superRefine((value, ctx) => {
        if (!value.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Email is required",
          });
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Invalid email format",
          });
        }
      }),
    },
    // password: {
    //   label: "Password",
    //   type: "password",
    //   placeholder: "Enter password",
    //   defaultValue: data.password,
    //   required: true,
    //   validation: z.string().min(4, "Password must be at least 4 characters"),
    // },
    sdkKey: {
      label: "SDK Key",
      type: "text",
      placeholder: "Enter SDK Key",
      defaultValue: data.sdkKey,
      required: true,
      normalize: (val) => val.replace(/\s+/g, ""),
      validation: z.string().superRefine((value, ctx) => {
        if (!value.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "SDK Key is required",
          });
        }
      }),
    },
    sdkSecret: {
      label: "SDK Secret",
      type: "text",
      placeholder: "Enter SDK Secret",
      defaultValue: data.sdkSecret,
      required: true,
      normalize: (val) => val.replace(/\s+/g, ""),
      validation: z.string().superRefine((value, ctx) => {
        if (!value.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "SDK Secret is required",
          });
        }
      }),
    },
    apiKey: {
      label: "API Key",
      type: "text",
      placeholder: "Enter API Key",
      fieldType: ["Audio"],
      defaultValue: data.apiKey,
      required: true,
      normalize: (val) => val.replace(/\s+/g, ""),
      validation: z.string().superRefine((value, ctx) => {
        if (!value.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "API Key is required",
          });
        }
      }),
    },
    apiSecret: {
      label: "API Secret",
      type: "text",
      placeholder: "Enter API Secret",
      fieldType: ["Audio"],
      defaultValue: data.apiSecret,
      required: true,
      normalize: (val) => val.replace(/\s+/g, ""),
      validation: z.string().superRefine((value, ctx) => {
        if (!value.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "API Secret is required",
          });
        }
      }),
    },
    accountId: {
      label: "Account ID",
      type: "text",
      placeholder: "Enter Account ID",
      fieldType: ["Audio"],
      defaultValue: data.accountId,
      required: true,
      validation: z.string().superRefine((value, ctx) => {
        if (!value.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Account ID is required",
          });
        }
      }),
    },
  };

  // 🔹 Filter fields based on tenant type
  const filteredFormConfig = Object.fromEntries(
    Object.entries(formConfig).filter(([_, config]) => {
      if (!config.fieldType || config.fieldType.length === 0) return true;
      if (isVideoTenant && config.fieldType.includes("Video")) return true;
      if (!isVideoTenant && config.fieldType.includes("Audio")) return true;
      return false;
    })
  );

  return (
    <MutationHandler
      mutationFn={(formData) =>
        apiPut(`${API_URL}/api/zooms/${data.id}`, formData)
      }
      onCancel={onCancel}
      onSuccess={onSuccess}
    >
      {(mutation) => (
        <ZoomEditForm
          formConfig={filteredFormConfig}
          onSubmit={(value) => mutation.mutate(value)}
          isLoading={mutation.isPending}
          onCancel={onCancel}
        />
      )}
    </MutationHandler>
  );
}

function ZoomEditForm({ formConfig, onSubmit, isLoading, onCancel }) {
  const form = useGeneratedForm(formConfig, async ({ value }) => {
    onSubmit(value);
  });

  return (
    <DynamicForm
      form={form}
      formConfig={formConfig}
      isLoading={isLoading}
      onCancel={onCancel}
      buttonText={"Edit Account"}
    />
  );
}
